/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Row, Col, Table, InputGroup } from "react-bootstrap";
import axios from "axios";
import config from "../../config";
import { toast } from "react-toastify";

const AddBatchModal = ({ show, handleClose, refreshBatches }) => {
  const [requests, setRequests] = useState([]);
  const [selectedRequestId, setSelectedRequestId] = useState("");
  const [boms, setBoms] = useState([]);
  const [formData, setFormData] = useState({
    label: "",
    bomId: "",
    inputs: [],
    stages: [],
    outputs: [],
  });
  const [showOutputs, setShowOutputs] = useState(false);

  const [rowLocations, setRowLocations] = useState({});
 
  useEffect(() => {
    if (show) {
      fetchRequests();
      fetchBoms(); // keep as fallback
    }
  }, [show]);

  const fetchRequests = async () => {
    try {
      const res = await axios.get(
        `${config.API_BASE_URL}/production-requests?status=PENDING,APPROVED`
      );
      setRequests(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    if (formData.bomId && formData.inputs.length > 0) {
      handleCalculateOutputs(); // Recalculate outputs when BOM or inputs change
    }
  }, [formData.inputs, formData.bomId]); // Trigger recalculation when BOM or inputs change

  const fetchBoms = async () => {
    try {
      const res = await axios.get(`${config.API_BASE_URL}/process-definitions`);
      setBoms(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchLocations = async (productId, rowIndex) => {
    try {
      const res = await axios.get(
        `${config.API_BASE_URL}/new-warehouses/storage/products?productId=${productId}`
      );

      setRowLocations((prev) => ({
        ...prev,
        [rowIndex]: res.data, // store only for that row
      }));
    } catch (err) {
      console.error(err);
    }
  };

  const handleRequestChange = async (requestId) => {
    setSelectedRequestId(requestId);
    if (!requestId) return;

    const res = await axios.get(
      `${config.API_BASE_URL}/production-requests/${requestId}`
    );
    const request = res.data;

    // Prefill from request
    const initialInputs = request.rawMaterials.map((rm) => ({
      materialId: rm.materialId._id || rm.materialId,
      quantity: rm.requiredQuantity,
      unit: rm.unit,
      locationId: "",
      packingMaterialId: "",
      quantityPerBag: "",
      numberOfBags: "",
      outerPackingMaterialId: "",
      outerNumberOfBags: "",
      selectedPoLotDetails: null,
    }));

    const initialStages = request.bomId.processingStages.map((s) => ({
      stageName: s.stageName,
      plantLocation: s.plantLocation,
      status: "pending",
      notes: "",
    }));

    const initialOutputs = request.bomId.outputs.map((output) => ({
      productId: output.productId._id,
      productName: output.productId.productName,
      percentage: output.percentage,
      projectedOutputPerHour: output.projectedOutput,
      unit: output.productId.unitId.unitCode,
      calculatedOutput: 0,
    }));

    setFormData({
      label: request.productId.groupId.groupName,
      bomId: request.bomId._id,
      inputs: initialInputs,
      stages: initialStages,
      outputs: initialOutputs,
      productionRequestId: request._id, // link batch to request
    });

    // Fetch locations for each material
    request.rawMaterials.forEach((rm, idx) => {
      fetchLocations(rm.materialId._id, idx);
    });
  };

  const handleBomChange = (bomId) => {
    const selected = boms.find((b) => b._id === bomId);
    if (!selected) return;

    const initialInputs = selected.rawMaterials.map((rm) => ({
      materialId: rm.materialId._id || rm.materialId,
      quantity: rm.quantity,
      unit: rm.unit,
      locationId: "", // user will select
      packingMaterialId: "",
      quantityPerBag: "",
      numberOfBags: "",
      outerPackingMaterialId: "",
      outerNumberOfBags: "",
      selectedPoLotDetails: null,
    }));

    const initialStages = selected.processingStages.map((s) => ({
      stageName: s.stageName,
      plantLocation: s.plantLocation,
      status: "pending",
      notes: "",
    }));

    const initialOutputs = selected.outputs.map((output) => ({
      productId: output.productId._id,
      productName: output.productId.productName,
      percentage: output.percentage,
      projectedOutputPerHour: output.projectedOutput,
      unit: output.productId.unitId.unitCode,
      calculatedOutput: 0,
    }));

    setFormData((prev) => ({
      ...prev,
      bomId,
      inputs: initialInputs,
      stages: initialStages,
      outputs: initialOutputs,
    }));

    // Fetch locations for each material (as you already do)
    selected.rawMaterials.forEach((rm, idx) => {
      fetchLocations(rm.materialId._id, idx);
    });
  };

  const handleMaterialChange = async (idx, materialId) => {
    handleInputChange(idx, "materialId", materialId);

    const selectedMaterial = boms
      .find((b) => b._id === formData.bomId)
      ?.rawMaterials.find((rm) => rm.materialId._id === materialId);

    if (selectedMaterial) {
      handleInputChange(idx, "unit", selectedMaterial.unit);
    }

    fetchLocations(materialId, idx); // row-specific
    handleCalculateOutputs();
  };

  const adjustQuantityToAvailable = (inputs, index) => {
    const input = inputs[index];
    const poLot = input.selectedPoLotDetails;

    if (poLot && input.locationId && input.poId) {
        // If packed, validate against available bags
        if (poLot.packingMaterial) {
            let currentBags = parseFloat(input.numberOfBags) || 0;
            if (currentBags > poLot.availableBags) {
                input.numberOfBags = poLot.availableBags;
                input.quantity = input.numberOfBags * poLot.quantityPerBag;
            } else {
                input.quantity = currentBags * poLot.quantityPerBag;
            }
        } else {
            // Unpacked, validate against available quantity
            let currentQty = parseFloat(input.quantity) || 0;
            if (currentQty > poLot.availableQuantity) {
                input.quantity = poLot.availableQuantity;
            }
        }
    } else {
        // If no poLot selected, ensure quantity and bags are cleared/reset
        if (input.packingMaterialId) { // If it was previously packed
            input.numberOfBags = '';
            input.quantity = '';
        } else { // Unpacked
            input.quantity = '';
        }
    }
    return inputs;
  };

  const handleInputChange = (index, field, value) => {
    const updated = [...formData.inputs];
    updated[index][field] = value;

    if (field === "poId") {
      const rowLocs = rowLocations[index] || [];
      const selectedLocation = rowLocs.find((loc) => loc._id === updated[index].locationId);
      const poLot = selectedLocation?.poLots.find((lot) => lot.poId === value);
      updated[index].selectedPoLotDetails = poLot;
      // Also update packing details if a poLot is selected
      if (poLot) {
        updated[index].packingMaterialId = poLot.packingMaterial?._id || "";
        updated[index].quantityPerBag = poLot.packingMaterial ? poLot.quantityPerBag : "";
        updated[index].numberOfBags = ""; // Reset bags when PO changes
        updated[index].outerPackingMaterialId = poLot.outerPackingMaterial?._id || "";
        updated[index].outerNumberOfBags = ""; // Reset outer bags when PO changes
        updated[index].unit = poLot.packingMaterial?.unit || updated[index].unit;
      } else {
         // Clear packing details if no poLot selected
        updated[index].packingMaterialId = "";
        updated[index].quantityPerBag = "";
        updated[index].numberOfBags = "";
        updated[index].outerPackingMaterialId = "";
        updated[index].outerNumberOfBags = "";
        updated[index].selectedPoLotDetails = null;
      }
    } else if (field === "numberOfBags") {
      const poLot = updated[index].selectedPoLotDetails;
      if (poLot && poLot.packingMaterial && updated[index].quantityPerBag) {
        updated[index].quantity = parseFloat(value || 0) * parseFloat(updated[index].quantityPerBag);
      }
    } else if (field === "quantityPerBag") {
       const poLot = updated[index].selectedPoLotDetails;
       if (poLot && poLot.packingMaterial && updated[index].numberOfBags) {
          updated[index].quantity = parseFloat(value || 0) * parseFloat(updated[index].numberOfBags);
       }
    }
    
    const adjustedInputs = adjustQuantityToAvailable(updated, index);
    setFormData((prev) => ({ ...prev, inputs: adjustedInputs }));
  };

  const addMaterialRow = () => {
    setFormData((prev) => {
      const updatedInputs = [
        ...prev.inputs,
        {
          materialId: "",
          quantity: "",
          unit: "",
          locationId: "",
          poId: "",
          packingMaterialId: "",
          quantityPerBag: "",
          numberOfBags: "",
          outerPackingMaterialId: "",
          outerNumberOfBags: "",
          selectedPoLotDetails: null,
        },
      ];
      return {
        ...prev,
        inputs: updatedInputs,
      };
    });

    handleCalculateOutputs();
  };

  const handleCalculateOutputs = () => {
    const totalInputQuantity = formData.inputs.reduce((sum, input) => {
      const selectedMaterial = boms
        .find((b) => b._id === formData.bomId)
        ?.rawMaterials.find((rm) => rm.materialId._id === input.materialId);

      const materialQuantity = selectedMaterial
        ? parseFloat(input.quantity)
        : 0;
      return sum + materialQuantity;
    }, 0);

    const updatedOutputs = formData.outputs.map((output) => {
      const calculatedOutput = (totalInputQuantity * output.percentage) / 100;
      return { ...output, calculatedOutput };
    });

    setFormData((prev) => ({
      ...prev,
      outputs: updatedOutputs,
    }));
  };

  const handleSubmit = async () => {
    try {
      // Filter out temporary `selectedPoLotDetails` before sending to backend
      const inputsToSend = formData.inputs.map(({ selectedPoLotDetails, ...rest }) => {
        return {
          ...rest,
          packingMaterialId: rest.packingMaterialId || null,
          outerPackingMaterialId: rest.outerPackingMaterialId || null,
          poId: rest.poId || null,
          quantityPerBag: rest.quantityPerBag === "" ? null : rest.quantityPerBag,
          numberOfBags: rest.numberOfBags === "" ? null : rest.numberOfBags,
          outerNumberOfBags: rest.outerNumberOfBags === "" ? null : rest.outerNumberOfBags,
        };
      });
      
      await axios.post(`${config.API_BASE_URL}/production-batches`, {
        ...formData,
        inputs: inputsToSend,
        productionRequestId:
          selectedRequestId || formData.productionRequestId || null,
      });
      toast.success("Batch created successfully");
      handleClose();
      refreshBatches();
    } catch (err) {
      toast.error("Failed to create batch");
      console.error(err);
    }
  };

  return (
    <Modal show={show} onHide={handleClose} size="xl" fullscreen="lg-down" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>Add Production Batch</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <Row className="mb-4 gy-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label>Production Request *</Form.Label>
                <Form.Select
                  value={selectedRequestId}
                  onChange={(e) => handleRequestChange(e.target.value)}
                >
                  <option value="">-- Select Production Request --</option>
                  {requests.map((req) => (
                    <option key={req._id} value={req._id}>
                      {req.requestNumber} - {req.productId.productName || req.productId.name} (
                      {req.quantityRequired} {req.productId.unitId.unitCode})
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>BOM (Process Definition) *</Form.Label>
                <Form.Select readOnly={true}
                  value={formData.bomId}
                  onChange={(e) => handleBomChange(e.target.value)}
                >
                  <option value="">-- Select BOM --</option>
                  {boms.map((bom) => (
                    <option key={bom._id} value={bom._id}>
                      {bom.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Batch Label *</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.label}
                  onChange={(e) =>
                    setFormData({ ...formData, label: e.target.value })
                  }
                  requiredF
                />
              </Form.Group>
            </Col>
          </Row>
          {formData.bomId && (
            <>
              <hr />
              <div className="mt-4 mb-3 d-flex justify-content-between gap-2 flex-wrap align-items-center">
                <h5 className="mt-0">Input Materials</h5>
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={addMaterialRow}
                >
                  + Add Material
                </Button>
              </div>
              <Table bordered responsive size="sm">
                <thead>
                  <tr>
                    <th>Material</th>
                    <th>Source Location</th>
                    <th>PO/Vendor & Packing</th>
                    <th>Qty Per Bag</th>
                    <th>Quantity (Total)</th>
                    <th>No. of Bags</th>
                    <th>Outer Packing/Bags</th>
                  </tr>
                </thead>
                <tbody>
                  {formData.inputs.map((input, idx) => {
                    const selectedMaterial = boms
                      .find((b) => b._id === formData.bomId)
                      ?.rawMaterials.find(
                        (rm) => rm.materialId._id === input.materialId
                      );

                    const unit = input.unit; // Use unit from input state

                    // ✅ row-specific locations
                    const rowLocs = rowLocations[idx] || [];

                    // get poLots for the chosen location
                    const selectedLocation = rowLocs.find(
                      (loc) => loc._id === input.locationId
                    );
                    const poLots = selectedLocation
                      ? selectedLocation.poLots
                      : [];

                    const selectedPoLot = input.selectedPoLotDetails;

                    return (
                      <tr key={idx}>
                        {/* Material */}
                        <td>
                          <Form.Select
                            value={input.materialId}
                            onChange={(e) =>
                              handleMaterialChange(idx, e.target.value)
                            }
                            style={{ minWidth: '115px' }}
                          >
                            <option value="">-- Select --</option>
                            {boms
                              .find((b) => b._id === formData.bomId)
                              ?.rawMaterials.map((rm) => (
                                <option
                                  key={rm.materialId._id}
                                  value={rm.materialId._id}
                                >
                                  {rm.materialId.name ||
                                    rm.materialId.productName}
                                </option>
                              ))}
                          </Form.Select>
                        </td>

                        {/* Location */}
                        <td>
                          <Form.Select
                            value={input.locationId}
                            onChange={(e) =>
                              handleInputChange(
                                idx,
                                "locationId",
                                e.target.value
                              )
                            }
                            style={{ minWidth: '115px' }}
                          >
                            <option value="">-- Select Location --</option>
                            {(rowLocations[idx] || []).map((loc) => (
                              <option key={loc._id} value={loc._id}>
                                {loc.fullPath}
                              </option>
                            ))}
                          </Form.Select>
                        </td>

                        {/* PO/Vendor & Packing */}
                        <td>
                          <Form.Select
                            value={input.poId || ""}
                            onChange={(e) =>
                              handleInputChange(idx, "poId", e.target.value)
                            }
                            disabled={!input.locationId}
                            style={{ minWidth: '200px' }}
                          >
                            <option value="">-- Select PO/Packing --</option>
                            {poLots.map((lot) => (
                              <option key={`${lot.poId}-${lot.packingMaterial?._id || 'unpacked'}-${lot.outerPackingMaterial?._id || 'no_outer'}`} value={lot.poId}>
                                {lot.purchaseOrder.poNumber} -{" "}
                                {lot.purchaseOrder.vendor.name || lot.purchaseOrder.vendor}{" "}
                                {lot.packingMaterial ? `(${lot.packingMaterial.name}, ${lot.quantityPerBag} ${unit}/bag, ${lot.availableBags} bags)` : '(Unpacked)'}
                                {lot.outerPackingMaterial ? ` (Outer: ${lot.outerPackingMaterial.name}, ${lot.availableOuterBags} outer bags)` : ''}
                                (Available: {lot.availableQuantity} {unit})
                              </option>
                            ))}
                          </Form.Select>
                        </td>

                        {/* Qty Per Bag */}
                        <td>
                          {input.packingMaterialId && (
                            <InputGroup style={{ minWidth: '90px' }}>
                                <Form.Control
                                    type="number"
                                    value={input.quantityPerBag}
                                    readOnly
                                />
                                {unit && (
                                    <InputGroup.Text>{unit}</InputGroup.Text>
                                )}
                            </InputGroup>
                          )}
                        </td>

                        {/* Quantity (Total) - Read-only if packed, editable if unpacked */}
                        <td>
                            <InputGroup style={{ minWidth: '105px' }}>
                                <Form.Control
                                    type="number"
                                    value={input.quantity}
                                    min={0}
                                    max={input.selectedPoLotDetails?.availableQuantity || ""}
                                    placeholder={input.selectedPoLotDetails?.availableQuantity ? `Max ${input.selectedPoLotDetails.availableQuantity}` : ""}
                                    onChange={(e) => handleInputChange(idx, "quantity", e.target.value)}
                                    readOnly={!!input.packingMaterialId}
                                />
                                {unit && (
                                    <InputGroup.Text>{unit}</InputGroup.Text>
                                )}
                            </InputGroup>
                        </td>

                        {/* Number of Bags - Only if packing material is selected */}
                        <td>
                          {input.packingMaterialId && (
                            <InputGroup style={{ minWidth: '105px' }}>
                              <Form.Control
                                type="number"
                                value={input.numberOfBags}
                                min={0}
                                max={input.selectedPoLotDetails?.availableBags || ""}
                                placeholder={input.selectedPoLotDetails?.availableBags ? `Max ${input.selectedPoLotDetails.availableBags}` : ""}
                                onChange={(e) => handleInputChange(idx, "numberOfBags", e.target.value)}
                                disabled={!input.locationId || !input.poId}
                              />
                              <InputGroup.Text>Bags</InputGroup.Text>
                            </InputGroup>
                          )}
                        </td>

                        {/* Outer Packing/Bags */}
                        <td>
                          {input.outerPackingMaterialId && (
                            <div className="d-flex flex-column gap-1" style={{ minWidth: '120px' }}>
                               <small className="text-muted">{selectedPoLot?.outerPackingMaterial?.name}</small>
                               <InputGroup size="sm">
                                  <Form.Control
                                    type="number"
                                    value={input.outerNumberOfBags}
                                    min={0}
                                    max={selectedPoLot?.availableOuterBags || ""}
                                    placeholder={`Max ${selectedPoLot?.availableOuterBags || 0}`}
                                    onChange={(e) => handleInputChange(idx, "outerNumberOfBags", e.target.value)}
                                  />
                                  <InputGroup.Text>Outer</InputGroup.Text>
                               </InputGroup>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>

              <hr />
              <Button
                variant="outline-secondary"
                size="sm"
                className="mt-3"
                onClick={() => setShowOutputs(!showOutputs)}
              >
                {showOutputs ? "Hide Output" : "Show Output"}
              </Button>

              {showOutputs && (
                <>
                  <h5 className="mt-4 mb-3">Calculated Outputs</h5>
                  <Table bordered responsive size="sm">
                    <thead>
                      <tr>
                        <th>Output Product</th>
                        <th>Calculated Quantity</th>
                      </tr>
                    </thead>
                    <tbody>
                      {formData.outputs.map((output, idx) => (
                        <tr key={idx}>
                          <td>{output.productName}</td>
                          <td>
                            {output.calculatedOutput.toFixed(2)} {output.unit}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </>
              )}
            </>
          )}
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSubmit}>
          Save Batch
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default AddBatchModal;
